/**
 * Renders the Study Buddy's markdown-ish reply as clear, labelled sections:
 * headings, bullet / numbered lists, tables, golden-rule callouts, dividers.
 * Raw text is HTML-escaped before any inline formatting is applied.
 */
import {
  AlertTriangle, BookOpen, Bookmark, CheckCircle2, Footprints, LayoutList,
  Lightbulb, ListChecks, MessageCircleQuestion, Sparkles, FlaskConical, Search,
} from 'lucide-react';
import { escapeHtml, SafeHTML } from '@/lib/safeHtml';

const SECTION_STYLES = [
  [/imagine|real life|story|hook/i, Lightbulb, 'text-amber-600 bg-amber-50'],
  [/topic|chapter has|parts|contents/i, LayoutList, 'text-indigo-600 bg-indigo-50'],
  [/understand|concept|idea|what is|meaning/i, BookOpen, 'text-blue-600 bg-blue-50'],
  [/example|let's try|worked/i, FlaskConical, 'text-emerald-600 bg-emerald-50'],
  [/step|solution|how to/i, Footprints, 'text-teal-600 bg-teal-50'],
  [/what we know|given/i, Search, 'text-sky-600 bg-sky-50'],
  [/key point|summary|recap/i, ListChecks, 'text-violet-600 bg-violet-50'],
  [/remember|exam|formula|date|definition/i, Bookmark, 'text-rose-600 bg-rose-50'],
  [/mistake|careful|trap/i, AlertTriangle, 'text-orange-600 bg-orange-50'],
  [/answer/i, CheckCircle2, 'text-emerald-700 bg-emerald-50'],
  [/your turn|question for you|try this|quick check/i, MessageCircleQuestion, 'text-fuchsia-600 bg-fuchsia-50'],
];

export function cleanLatex(text) {
  return text
    .replace(/\\\[/g, '\n').replace(/\\\]/g, '\n')
    .replace(/\\\(/g, '').replace(/\\\)/g, '')
    .replace(/\$\$([^$]+)\$\$/g, '\n$1\n')
    .replace(/\$([^$]+)\$/g, '$1')
    .replace(/\\Rightarrow/gi, ' \u2192 ')
    .replace(/\\rightarrow/gi, ' \u2192 ')
    .replace(/\\times/gi, ' \u00D7 ')
    .replace(/\\cdot/gi, ' \u00B7 ')
    .replace(/\\div/gi, ' \u00F7 ')
    .replace(/\\pm/gi, ' \u00B1 ')
    .replace(/\\neq/gi, ' \u2260 ')
    .replace(/\\leq/gi, ' \u2264 ').replace(/\\geq/gi, ' \u2265 ')
    .replace(/\\approx/gi, ' \u2248 ')
    .replace(/\\infty/gi, '\u221E').replace(/\\pi/gi, '\u03C0')
    .replace(/\\theta/gi, '\u03B8').replace(/\\alpha/gi, '\u03B1').replace(/\\beta/gi, '\u03B2')
    .replace(/\\sqrt\{([^}]+)\}/g, '\u221A($1)')
    .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '($1)/($2)')
    .replace(/\^{([^}]+)}/g, '^$1').replace(/_{([^}]+)}/g, '_$1')
    .replace(/\\text\{([^}]+)\}/g, '$1').replace(/\\mathrm\{([^}]+)\}/g, '$1')
    .replace(/\\quad/g, '  ').replace(/\\;/g, ' ').replace(/\\,/g, ' ').replace(/\\!/g, '')
    .replace(/\\\\/g, '\n').replace(/\\[{}]/g, '');
}

function inline(text) {
  return escapeHtml(text)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*\w])\*(?!\s)([^*]+?)\*(?!\w)/g, '$1<em>$2</em>')
    .replace(/`([^`]+)`/g, '<code class="bg-slate-100 px-1.5 py-0.5 rounded text-xs font-mono text-blue-700">$1</code>');
}

const Inline = ({ text }) => <SafeHTML html={inline(text)} />;

const HEADING = /^#{1,6}\s+(.*)$/;
const BOLD_HEADING = /^\*\*([^*]{2,70}?)\*\*:?$/;
const RULE = /^(-{3,}|\*{3,}|_{3,})$/;
const BULLET = /^[-*\u2022]\s+(.*)$/;
const NUMBER = /^(\d+)[.)]\s+(.*)$/;
const QUOTE = /^>\s?(.*)$/;
const TABLE_ROW = /^\|.*\|$/;
const TABLE_SEP = /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?$/;

function toBlocks(text) {
  const blocks = [];
  const push = (type, item) => {
    const last = blocks[blocks.length - 1];
    if (last && last.type === type && type !== 'heading' && type !== 'rule') last.items.push(item);
    else blocks.push({ type, items: [item] });
  };
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    let m;
    if (!line) { blocks.push({ type: 'gap', items: [] }); continue; }
    if ((m = line.match(HEADING))) push('heading', m[1].replace(/\*\*/g, '').trim());
    else if ((m = line.match(BOLD_HEADING))) push('heading', m[1].trim());
    else if (RULE.test(line)) push('rule', '');
    else if (TABLE_ROW.test(line)) { if (!TABLE_SEP.test(line)) push('table', line); }
    else if ((m = line.match(BULLET))) push('ul', m[1]);
    else if ((m = line.match(NUMBER))) push('ol', { n: m[1], text: m[2] });
    else if ((m = line.match(QUOTE))) push('quote', m[1]);
    else push('p', line);
  }
  return blocks.filter((b, i, all) => b.type !== 'gap' || (all[i - 1] && all[i - 1].type !== 'gap'));
}

function SectionHeading({ label, idx }) {
  const hit = SECTION_STYLES.find(([rx]) => rx.test(label));
  const Icon = hit ? hit[1] : Sparkles;
  const tone = hit ? hit[2] : 'text-slate-600 bg-slate-100';
  return (
    <div className={`flex items-center gap-2 ${idx ? 'mt-4' : 'mt-0.5'} mb-1.5`} data-testid="chat-section-heading">
      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-lg ${tone}`}>
        <Icon className="w-3.5 h-3.5" />
      </span>
      <span className="font-heading font-bold text-[0.95rem] text-slate-900 leading-snug">{label}</span>
    </div>
  );
}

function Table({ rows }) {
  const cells = rows.map(r => r.replace(/^\||\|$/g, '').split('|').map(c => c.trim()));
  const [head, ...body] = cells;
  return (
    <div className="my-2 overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full text-xs">
        <thead className="bg-slate-50">
          <tr>{head.map((c, i) => <th key={i} className="px-3 py-2 text-left font-heading font-semibold text-slate-700"><Inline text={c} /></th>)}</tr>
        </thead>
        <tbody>
          {body.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              {r.map((c, j) => <td key={j} className="px-3 py-2 align-top text-slate-700"><Inline text={c} /></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Block({ block, idx }) {
  const { type, items } = block;
  if (type === 'heading') return <SectionHeading label={items[0]} idx={idx} />;
  if (type === 'rule') return <hr className="my-3 border-slate-200/80" />;
  if (type === 'gap') return <div className="h-1.5" />;
  if (type === 'table') return <Table rows={items} />;
  if (type === 'quote') return (
    <div className="my-2 rounded-xl border-l-4 border-amber-400 bg-amber-50/70 px-3 py-2 text-slate-800" data-testid="chat-callout">
      {items.map((t, i) => <div key={i}><Inline text={t} /></div>)}
    </div>
  );
  if (type === 'ul') return (
    <ul className="my-1 space-y-1">
      {items.map((t, i) => (
        <li key={i} className="flex gap-2"><span className="mt-[0.7em] w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" /><span><Inline text={t} /></span></li>
      ))}
    </ul>
  );
  if (type === 'ol') return (
    <ol className="my-1 space-y-1.5">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2.5">
          <span className="mt-[0.2em] inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-heading font-bold flex-shrink-0">{it.n}</span>
          <span><Inline text={it.text} /></span>
        </li>
      ))}
    </ol>
  );
  return <p className="my-0.5">{items.map((t, i) => <span key={i}>{i > 0 && <br />}<Inline text={t} /></span>)}</p>;
}

export function FormattedMessage({ content }) {
  if (!content) return null;
  const parts = content.split(/(```[\s\S]*?```)/g);
  return parts.map((part, i) => {
    if (part.startsWith('```') && part.endsWith('```')) {
      const code = part.slice(3, -3).replace(/^\w*\n/, '');
      return <pre key={i} className="bg-slate-100 rounded-xl p-3 my-2 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-words border border-slate-200">{code}</pre>;
    }
    return toBlocks(cleanLatex(part)).map((b, j) => <Block key={`${i}-${j}`} block={b} idx={j} />);
  });
}

export default FormattedMessage;
